import * as THREE from 'three'
import { Scene } from './Scene'
import { CPointSet, CameraPoint } from './CameraPoints'
import { strToNumberArray } from './Utils'
import { EventEmitter } from 'events/events'
import { Events } from './Events'

class EditPoint {
  /**
   * 单个点位的构造函数
   * @param {object} point json配置文件内的points对象
   * @param {THREE.Texture} texture 单个点位所使用的纹理
   */
  constructor (point, texture) {
    /**
     * @type {number} 点位id
     */
    this.id = point.pointIndex
    /**
     * @type {THREE.Mesh} 点位Mesh
     */
    this.pointMesh = null
    /**
     * @type {THREE.Vector3} 点位位置信息转换为Vector3格式
     */
    this.position = new THREE.Vector3().fromArray(strToNumberArray(point.pointPosition)) // new THREE.Vector3(posA[0], posA[2], -posA[1])
    /**
     * @type {number} 点位网格的y轴位置（高度，一般是贴着地板）
     */
    this.meshY = point.pointFloor
    /**
     * @type {boolean} 点位是否可视
     */
    this.isAvailable = point.isVisible
    /**
     * @type {number} 点位类别（暂无应用）：0表示常规点 1表示入口点（不可关闭） 2表示关键点（不可关闭）//？？？
     */
    this.type = 1

    this.initEditPoint(texture)
  }

  /**
   * 创建一个可编辑的点位
   * @param {THREE.Texture} texture 单个点位所使用的纹理
   */
  initEditPoint (texture) {
    const staticPointM = new THREE.SpriteMaterial({ color: 0xffffff, map: texture })
    this.pointMesh = new THREE.Sprite(staticPointM)
    staticPointM.dispose()
    this.pointMesh.scale.set(170, 170, 1)
    this.pointMesh.position.set(
      this.position.x,
      this.meshY + 900,
      this.position.z
    )
    this.pointMesh.depthTest = true
    this.pointMesh.depthWrite = true
  }
}

export class EditPointSet {
  /**
   * 创建集合对象
   * @param {string} iconUrlSet 配置
   * @param {object} pointMsgs 配置
   * @param {CPointSet} cps 点位集合
   * @param {THREE.Scene} scene 需要被添加到的场景
   * @param {Array[]} mgraph 邻接矩阵
   */
  constructor (iconUrlSet, pointMsgs, cps, scene, mgraph) {
    /**
     * @type {CPointSet} 点位集合
     */
    this.cps = cps

    /**
     * @type {CameraPoint[]} CPS单例对象下的相机点位数组
     */
    this.CameraPoints = cps.CameraPoints
    /**
     * @type {EditPoint[]} EditPoints对象数组
     */
    this.EditPoints = []
    /**
     * @type {boolean} 是否显示编辑点位开关接口，前端编辑页面控制这个。只有开启的情况下才允许 显示 编辑点。隐藏编辑点则任何时候都可以进行。
     */
    this.showEdit = true
    /**
     * @type {THREE.Texture} 点位贴图，仅加载一次，全点位共用
     */
    this.iconTexture = new THREE.TextureLoader().load(iconUrlSet.iconTexture)
    // todo: 从pubilc读入
    /**
     * @type {THREE.Texture} 地面上禁用点位贴图
     */
    this.iconDisableTexture = new THREE.TextureLoader().load(iconUrlSet.iconDisableTexture)
    /**
     * @type {THREE.Texture} 悬空编辑可见贴图
     */
    this.onVisiableTexture = new THREE.TextureLoader().load(iconUrlSet.onVisiableTexture)
    /**
     * @type {THREE.Texture} 悬空编辑不可见贴图
     */
    this.offVisiableTexture = new THREE.TextureLoader().load(iconUrlSet.offVisiableTexture)
    /**
     * @type {THREE.Object3D[]} 每个编辑点网格都push到列表中，用于射线检测
     */
    this.intersectObj = []
    /**
     * @type {THREE.Object3D[]} 射线相交的mesh列表
     */
    this.interscetResult = []
    /**
     * @type {number[]} 不可隐藏的点位id
     */
    this.truePoints = []
    /**
     * @type {EventEmitter} 自定义事件监听器
     */
    this.emitter = null

    cps.MGraph = mgraph // 直接传引用，恢复mgraph为全点位开启状态
    this.initExtraPoints(pointMsgs, scene)
  }

  /**
   * 初始化编辑点位，仅开启编辑状态调用
   * @param {object} points 设置对象setting.json下的pointList对象
   * @param {THREE.Scene} scene 场景根节点
   */
  initExtraPoints (points, scene) {
    this.editGroup = new THREE.Group()

    const lineMaterial = new THREE.LineBasicMaterial({ color: 0xFFFFFF, linewidth: 1 })
    let ls = []

    // 重新初始化，添加关闭显示的点位
    this.editGroup = new THREE.Group()
    for (const pd of points) {
      const ep = new EditPoint(pd, pd.isVisible ? this.onVisiableTexture : this.offVisiableTexture)
      this.EditPoints.push(ep)
      this.editGroup.add(ep.pointMesh)
      if (!pd.isVisible) {
        const cp = new CameraPoint(pd, this.iconDisableTexture)
        this.CameraPoints.push(cp)
        this.editGroup.add(cp.pointMesh)
      }

      // 画白线
      ls = []
      const p = ep.pointMesh.position.clone()
      p.y -= 90 // 视线稍微往下一点
      ls.push(p)
      ls.push(this.cps.getCameraPoint(pd.pointIndex).pointMesh.position)
      const geometry = new THREE.BufferGeometry().setFromPoints(ls)
      const line = new THREE.Line(geometry, lineMaterial)
      this.editGroup.add(line)
      geometry.dispose()

      // 射线检测用
      this.intersectObj.push(ep.pointMesh)
    }
    scene.add(this.editGroup)
    this.editGroup.visible = false // 初始化永远不显示
    lineMaterial.dispose()
  }

  /**
   * 根据点位id获取可编辑点位对象
   * @param {number} id 点位id
   */
  getEditPoint (id) {
    for (const p of this.EditPoints) {
      if (p.id === id) {
        return p
      }
    }
  }

  /**
   * 显示所有编辑点
   */
  showEditPoint () { // 只有showEdit开启的情况下才允许 显示 编辑点。
    if (!this.showEdit) return
    this.editGroup.visible = true
  }

  /**
   * 隐藏所有编辑点
   */
  hideEditPoint () { // 隐藏编辑点则任何时候都可以进行。
    this.editGroup.visible = false
  }

  /**
   * 填充不可修改点列表
   * @param {[]} dictA 初始进入点的字典
   * @param {[]} listB 下方场景列表的列表
   */
  fillTruePoints (dictA, listB) {
    for (const key in dictA) {
      this.pushNoRepeat(this.truePoints, dictA[key].idx)
    }

    listB.filter(view => { // 建议采用这个，其不会对empty进行检测
      this.pushNoRepeat(this.truePoints, view.pointIndex)
    })
    // for (const view of listB) {
    //   this.pushNoRepeat(this.truePoints, view.pointIndex)
    // }
  }

  /**
   * 不重复插入数组
   * @param {Array} arr 数组
   * @param {number} value 值
   */
  pushNoRepeat (arr, value) {
    for (let i = 0; i < arr.length; ++i) {
      if (arr[i] === value) {
        return
      }
    }
    arr.push(value)
  }

  /**
   * 检测鼠标射线与点位编辑图标相交，获取目标点位信息。
   * @param {THREE.Raycaster} raycaster 鼠标射线检测与编辑点位贴图相交
   * @returns {THREE.Mesh} 返回第一个与射线相交的对象网格，若无则为null
   */
  interact (raycaster) {
    if (this.editGroup.visible) {
      this.interscetResult.length = 0
      raycaster.intersectObjects(this.intersectObj, false, this.interscetResult)
      if (this.interscetResult.length > 0) {
        return this.interscetResult[0]
      } else return null
    } else return null
  }

  /**
   * 处理点击编辑点位事件
   * @param {THREE.Mesh} mesh 传入目标点位的编辑模型
   */
  onClick (mesh) {
    // TODO 点击前判断该点位是否可以被设置成不可用
    for (const p of this.EditPoints) {
      if (p.pointMesh === mesh) {
        console.log('当前点击的是id为' + p.id + '的点位')

        for (const temp of this.truePoints) { // check是否可关闭
          if (temp === p.id) {
            console.log('此点位是场景点或进入点，不能关的嗷')
            // 点击失败了事件，暴露给ViewRoam.vue
            if (this.emitter) this.emitter.emit(Events.EPSclickPointFail, p.id)
            return
          }
        }

        // 发生一个事件，具体点了啥
        if (this.emitter) this.emitter.emit(Events.EPSclickPoint, p.id)

        if (p.isAvailable) {
          p.isAvailable = false
          p.pointMesh.material.map = this.offVisiableTexture
          this.cps.getCameraPoint(p.id).pointMesh.material.map = this.iconDisableTexture
          // ???
        } else {
          p.isAvailable = true
          p.pointMesh.material.map = this.onVisiableTexture
          this.cps.getCameraPoint(p.id).pointMesh.material.map = this.iconTexture
        }
      }
    }
  }

  /**
   * 外部vue传入监听器
   * @param {EventEmitter} emitter 自定义事件监听器
   */
  setEmitter (emitter) {
    if (!this.emitter) {
      this.emitter = emitter
      console.log('注册成功')
    } else {
      console.error('already has an emitter, set new emitter failed')
    }
  }

  /**
   * 保存按钮事件，前端做了
   */
  saveChanged () {
    // 保存按钮需要做的事情有什么？
    for (const p of this.CameraPoints) {
      if (!p.isVisible) {
        // 如果点位关闭
      }
    }
  }
}
